// src/components/PushNotificationSettings.js
import React, { useState, useEffect } from 'react';
import pushNotificationService from '../services/pushNotificationService';

/**
 * PushNotificationSettings Component
 * Allows users to enable/disable push notifications
 * 
 * Usage:
 * <PushNotificationSettings />
 * 
 * Optional props:
 * - onStatusChange: callback when notification status changes
 * - showTestButton: show test notification button (default: true)
 * - className: custom CSS class
 */
const PushNotificationSettings = ({
  onStatusChange,
  showTestButton = true,
  className = ''
}) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Check notification support and status on mount
  useEffect(() => {
    checkNotificationStatus();
  }, []);

  /**
   * Check if notifications are supported and get current status
   */
  const checkNotificationStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check browser support
      const supported = pushNotificationService.isSupported();
      setIsSupported(supported);

      if (!supported) {
        setLoading(false);
        return;
      }

      // Initialize service
      await pushNotificationService.initialize();

      // Get permission status
      const currentPermission = pushNotificationService.getPermissionStatus();
      setPermission(currentPermission);

      // Check if currently subscribed
      const subscribed = await pushNotificationService.isSubscribed();
      setIsEnabled(subscribed);

      // Call callback if provided
      if (onStatusChange) {
        onStatusChange({
          supported,
          enabled: subscribed,
          permission: currentPermission
        });
      }

    } catch (err) {
      console.error('Error checking notification status:', err);
      setError('Failed to check notification status');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Enable push notifications
   */
  const enableNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Subscribe to push notifications
      await pushNotificationService.subscribe();

      // Update state
      setIsEnabled(true);
      setPermission('granted');
      setSuccess('Push notifications enabled successfully!');

      // Call callback if provided
      if (onStatusChange) {
        onStatusChange({
          supported: isSupported,
          enabled: true,
          permission: 'granted'
        });
      }

    } catch (err) {
      console.error('Error enabling notifications:', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to enable push notifications';
      
      if (err.message.includes('denied')) {
        errorMessage = 'Notification permission denied. Please enable notifications in your browser settings.';
      } else if (err.message.includes('Authentication')) {
        errorMessage = 'Please log in to enable push notifications.';
      }
      
      setError(errorMessage);
      
      // Update permission state if it was denied
      const currentPermission = pushNotificationService.getPermissionStatus();
      setPermission(currentPermission);
      
    } finally {
      setLoading(false);
    }
  };

  /**
   * Disable push notifications
   */
  const disableNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Unsubscribe from push notifications
      await pushNotificationService.unsubscribe();

      // Update state
      setIsEnabled(false);
      setSuccess('Push notifications disabled successfully!');

      // Call callback if provided
      if (onStatusChange) {
        onStatusChange({
          supported: isSupported,
          enabled: false,
          permission
        });
      }

    } catch (err) {
      console.error('Error disabling notifications:', err);
      setError('Failed to disable push notifications');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send test notification
   */
  const sendTestNotification = async () => {
    try {
      setTestLoading(true);
      setError(null);
      setSuccess(null);

      await pushNotificationService.sendTestNotification();
      setSuccess('Test notification sent! You should receive it shortly.');

    } catch (err) {
      console.error('Error sending test notification:', err);
      setError('Failed to send test notification');
    } finally {
      setTestLoading(false);
    }
  };

  /**
   * Toggle notification state
   */
  const handleToggle = () => {
    if (isEnabled) {
      disableNotifications();
    } else {
      enableNotifications();
    }
  };

  // If notifications are not supported, show a message
  if (!isSupported) {
    return (
      <div className={`push-notification-settings ${className}`}>
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          Push notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, or Safari.
        </div>
      </div>
    );
  }

  // If permission was permanently denied, show instructions
  if (permission === 'denied') {
    return (
      <div className={`push-notification-settings ${className}`}>
        <div className="alert alert-danger">
          <i className="bi bi-bell-slash-fill me-2"></i>
          <strong>Notifications Blocked</strong>
          <p className="mb-0 mt-2">
            You have blocked notifications for this site. To enable them:
          </p>
          <ol className="mt-2 mb-0">
            <li>Click the lock icon in your browser's address bar</li>
            <li>Find "Notifications" in the permissions list</li>
            <li>Change it to "Allow"</li>
            <li>Refresh this page</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className={`push-notification-settings ${className}`}>
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">
                <i className="bi bi-bell-fill me-2" style={{ color: '#ff6800' }}></i>
                Push Notifications
              </h5>
              <p className="text-muted mb-0 small">
                Receive notifications about important updates
              </p>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="pushNotificationToggle"
                checked={isEnabled}
                onChange={handleToggle}
                disabled={loading}
                style={{
                  width: '3em',
                  height: '1.5em',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </div>

          {/* Status indicator */}
          <div className="mb-3">
            <small className="text-muted">
              Status: {' '}
              <span className={`badge ${isEnabled ? 'bg-success' : 'bg-secondary'}`}>
                {loading ? 'Loading...' : isEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </small>
          </div>

          {/* Error message */}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-circle-fill me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Success message */}
          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Test notification button */}
          {showTestButton && isEnabled && (
            <div className="mt-3">
              <button
                className="btn btn-sm"
                style={{
                  backgroundColor: '#ff6800',
                  color: 'white',
                  border: 'none'
                }}
                onClick={sendTestNotification}
                disabled={testLoading}
              >
                {testLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send-fill me-2"></i>
                    Send Test Notification
                  </>
                )}
              </button>
            </div>
          )}

          {/* Information text */}
          {isEnabled && (
            <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
              <small className="text-muted">
                <i className="bi bi-info-circle-fill me-2"></i>
                You will receive notifications for:
                <ul className="mt-2 mb-0">
                  <li>Task mentions and assignments</li>
                  <li>Event updates and reminders</li>
                  <li>Important system notifications</li>
                  <li>Social media post status updates</li>
                </ul>
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PushNotificationSettings;
